import classNames from 'classnames';
import { useEffect } from 'react';
import type { CampaignIntegrationModel } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { Loader } from 'shared/ui/Loader';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { MutatableSwitcher, getRunCampaignError } from 'shared/ui/Switcher';
import { useSendTestEmailLead, useSendTestLead, useUpdateIntegration } from 'entities/campaign-integrations';

interface IntegrationProps {
  index: number;
  disableActivationSwitcher?: boolean;
  campaignId: number;
  value: CampaignIntegrationModel;
  onIntegrationClick?: (id: number) => void;
  isEmail?: boolean;
  isOpenedTestLead: boolean;
  openTestLead: (id: string) => void;
  closeTestLead: (id: string) => void;
}

export function Integration({
  index,
  value,
  campaignId,
  disableActivationSwitcher,
  onIntegrationClick,
  isEmail = false,
  isOpenedTestLead,
  openTestLead,
  closeTestLead,
}: IntegrationProps) {
  const sendTestLeadQuery = useSendTestLead();
  const sendTestEmailLeadQuery = useSendTestEmailLead();
  const leadResponse = sendTestLeadQuery.data?.data || sendTestEmailLeadQuery.data?.data;
  const leadError = sendTestLeadQuery.error || sendTestEmailLeadQuery.error;
  const isSuccessLead = leadResponse?.result;
  const toggleName = `isActive.${value.id}`;
  const useUpdateFn = () => useUpdateIntegration(campaignId, value.id);
  const isSendTestShow = !sendTestLeadQuery.isLoading && !sendTestEmailLeadQuery.isLoading;
  const isSendTestLoading = !isSendTestShow;

  const handleSendClick = () => {
    if (!isOpenedTestLead) {
      if (isEmail) {
        sendTestEmailLeadQuery.mutate({
          urlParams: {
            campaignId,
          },
          payload: {
            email: value.label,
          },
        });
      } else {
        sendTestLeadQuery.mutate({
          urlParams: {
            campaignIntegrationsId: value.id,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (leadResponse || leadError) {
      openTestLead(value.label);
    }
  }, [leadResponse, leadError]);

  return (
    <div
      key={value.id}
      className={classNames('border rounded-md mb-2 px-4', {
        'border-deep-blue-100': !isOpenedTestLead,
        'border-deep-green-100': isOpenedTestLead && isSuccessLead,
        'border-red-100': isOpenedTestLead && !isSuccessLead,
        'hover:border-deep-blue-300': !isOpenedTestLead && onIntegrationClick,
      })}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-14 flex-1 items-center justify-between">
          <div
            {...getDataAutoTestAttributes([`title-integration-${index + 1}`]).attributes}
            className={classNames('h-full w-full  pt-[18px] text-sm', {
              'cursor-pointer': !!onIntegrationClick,
            })}
            onClick={() => onIntegrationClick?.(value.id)}
          >
            {value.label}
          </div>
        </div>
        <div className="relative h-6 w-6">
          {isSendTestShow && (
            <MaterialIcon
              className={classNames('select-none', {
                'text-deep-blue-500 cursor-pointer ': !isOpenedTestLead,
                'text-deep-blue-200': isOpenedTestLead,
              })}
              {...getDataAutoTestAttributes([`button-integration-send-test-lead-${index + 1}`])}
              onClick={handleSendClick}
            >
              forward_to_inbox
            </MaterialIcon>
          )}
          {isSendTestLoading && <Loader className="absolute left-[-8px] top-[-4px] scale-50" />}
        </div>
        {!disableActivationSwitcher && (
          <div className="ml-6">
            <MutatableSwitcher
              useUpdate={useUpdateFn}
              buildMutateArgs={(isActive) => ({ payload: { isActive } })}
              getErrorMessage={getRunCampaignError}
              name={toggleName}
              isChecked={value.isActive}
              {...getDataAutoTestAttributes([`switcher-integration-activate-${index + 1}`])}
            />
          </div>
        )}
      </div>
      <div
        className="overflow-hidden"
        style={{
          maxHeight: isOpenedTestLead ? 300 : 0,
          transition: 'max-height 0.2s ease-out',
        }}
        {...getDataAutoTestAttributes(['info-integration']).attributes}
      >
        <div className="h-[1px] bg-slate-100" />
        {isSuccessLead && (
          <div className="mt-4 flex gap-4">
            <div>
              <MaterialIcon className="text-[20px] text-deep-green-500">check_circle</MaterialIcon>
            </div>
            <div className="text-sm">
              <div className="mb-2 font-medium">Test lead has been sent</div>
              <div className="mb-2 flex flex-col">
                <p>
                  <span className="font-medium">Name: </span>
                  {leadResponse.lead.name}
                </p>
                <p>
                  <span className="font-medium">Email: </span>
                  {leadResponse.lead.email}
                </p>
              </div>
              <div>
                Please contact the contractor to confirm they received the test lead with the email shown above.
              </div>
              <Button
                className="mb-5 mt-4 w-[77px]"
                variant="outline"
                size="medium"
                onClick={() => {
                  closeTestLead(value.label);
                }}
              >
                Got it
              </Button>
            </div>
          </div>
        )}
        {!isSuccessLead && (
          <div className="mt-4 flex gap-4">
            <div>
              <MaterialIcon className="text-[20px] text-red-500">error_outline</MaterialIcon>
            </div>
            <div className="text-sm">
              <div className="mb-2 font-medium">{isEmail ? 'Something went wrong' : 'Test lead has not been sent'}</div>
              <div>Please, check the integration settings and try again.</div>
              <Button
                className="mb-5 mt-4 w-36"
                variant="outline"
                size="medium"
                onClick={() => {
                  closeTestLead(value.label);
                  onIntegrationClick?.(value.id);
                }}
              >
                {isEmail ? 'Got it' : 'Edit integration'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
